import React, { useState, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Search } from "lucide-react";
import { useOrganizationsStore } from "@/stores/organizationsStore";
import {
  SentenceFilterConfig,
  UNMAPPABLE_QUERY_MESSAGE,
} from "@/components/SentenceFilter";
import {
  describeFilters,
  FilterInterpretationError,
  interpretFilterQuery,
} from "@/lib/filterQuery";

interface CompactSentenceFilterProps<TFilterResult, TQueryResults> {
  config: SentenceFilterConfig<TFilterResult, TQueryResults>;
  onFiltersApplied: (
    filters: TFilterResult,
    results: TQueryResults,
    query: string
  ) => void;
}

export interface CompactSentenceFilterRef {
  clearQuery: () => void;
}

function CompactSentenceFilter<TFilterResult, TQueryResults>(
  {
    config,
    onFiltersApplied,
  }: CompactSentenceFilterProps<TFilterResult, TQueryResults>,
  ref: React.Ref<CompactSentenceFilterRef>
) {
  const [query, setQuery] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interpretation, setInterpretation] = useState<string | null>(null);

  const { selectedOrganizationId } = useOrganizationsStore();

  // Expose clear function to parent
  React.useImperativeHandle(ref, () => ({
    clearQuery: () => {
      setQuery("");
      setError(null);
      setInterpretation(null);
    }
  }));

  // Process natural language query using LLM
  const processQuery = async (queryText: string) => {
    if (!queryText.trim()) return;
    if (!selectedOrganizationId) {
      setError("No organization selected");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setInterpretation(null);

    try {
      const { filters, filterCount } = await interpretFilterQuery<TFilterResult>(
        config,
        queryText
      );

      if (filterCount === 0) {
        setError(UNMAPPABLE_QUERY_MESSAGE);
        return;
      }

      setInterpretation(describeFilters(filters));
      await fetchResults(filters, queryText);
    } catch (error) {
      if (error instanceof FilterInterpretationError) {
        setError(error.message);
      } else {
        console.error("Failed to process query:", error);
        setError("Failed to process query. Please try again.");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Fetch results using the configured fetch function
  const fetchResults = async (filters: TFilterResult, queryText?: string) => {
    if (!selectedOrganizationId) return;

    setIsLoadingResults(true);
    try {
      const results = await config.fetchResults(selectedOrganizationId, filters);
      onFiltersApplied(filters, results, queryText || "");
      setQuery(""); // Clear input on success
    } catch (error) {
      console.error("Failed to fetch results:", error);
      setError(`Failed to fetch ${config.entityName} results.`);
    } finally {
      setIsLoadingResults(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    processQuery(query);
  };

  const isLoading = isProcessing || isLoadingResults;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <div className="flex w-full flex-col gap-2 md:flex-row md:items-center">
        <Input
          placeholder={config.placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (query.trim() && !isLoading) {
                processQuery(query);
              }
            }
          }}
          disabled={isLoading}
          className="w-full md:flex-1"
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          disabled={!query.trim() || isLoading}
          className="w-full whitespace-nowrap md:w-auto"
        >
          {isLoading ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Searching...
            </>
          ) : (
            <>
              <Search className="mr-2 h-4 w-4" />
              Search
            </>
          )}
        </Button>
      </div>
      {interpretation && !error && (
        <div className="text-xs text-muted-foreground break-words">
          Interpreted as: {interpretation}
        </div>
      )}
      {error && (
        <div className="text-sm text-destructive">
          {error}
        </div>
      )}
    </form>
  );
}

export default forwardRef(CompactSentenceFilter) as <TFilterResult, TQueryResults>(
  props: CompactSentenceFilterProps<TFilterResult, TQueryResults> & {
    ref?: React.Ref<CompactSentenceFilterRef>;
  }
) => ReturnType<typeof CompactSentenceFilter>;
